import { IExpressionResolver, ResolutionErrorKind } from 'app/model/expressions/expression-resolution';
import { Activity } from 'app/model/pipeline/activity';
import { Owned } from 'app/model/base/named';
import { getActivityAncestryWithExclusions } from 'app/model/expressions/expression-utils';
import { BracketExpressionResolver, ILocatedEntity, IProperty } from './bracket-expression-resolver';
import { stringsAreEqualCaseInsensitive } from 'app/shared/utils/string.util';

/**
 * Resolves activity output expressions. e.g.: @activity('Lookup1').output.value[0].Prop_0
 */
export class ActivityOutputResolver extends BracketExpressionResolver {
    protected readonly _entityFoundExcludedError = ResolutionErrorKind.ActivityNoOutput;
    protected readonly _entityNotReferenceableResolutionError = ResolutionErrorKind.ActivityNotReferencable;
    protected readonly _missingEntityNameResolutionError = ResolutionErrorKind.MissingActivityName;

    /**
     * Initializes a new instance of the ActivityOutputResolver class.
     * @param _entity The entity owning the field being parameterized.
     * @param _expressionResolversCallback Provides supported expression resolvers used to resolve function arguments.
     */
    public constructor(_entity: Owned, _expressionResolversCallback: () => IExpressionResolver[]) {
        super(_entity, _expressionResolversCallback);
    }

    protected getPrefix(ignoreStart: boolean): string {
        return (ignoreStart ? '' : '@') + 'activity';
    }

    protected locateEntity(fromEntity: Owned, activityName: string): ILocatedEntity {
        while (fromEntity && !(fromEntity instanceof Activity)) {
            fromEntity = fromEntity.owner;
        }
        if (!fromEntity) {
            return null;
        }
        const ancestry = getActivityAncestryWithExclusions(fromEntity as Activity);
        const ancestorActivity = ancestry.activities.find((a) => stringsAreEqualCaseInsensitive(a.name, activityName));

        // Found activity that is referencable.
        if (ancestorActivity) {
            return { isExcluded: false, named: ancestorActivity };
        }

        const excludedActivity = ancestry.exclusions.find((a) => stringsAreEqualCaseInsensitive(a.name, activityName));

        // Found activity that is an ancestor but has no output.
        if (excludedActivity) {
            return {isExcluded: true, named: excludedActivity };
        }

        return null;
    }

    /**
     * Checks if the first property is 'output' and if the entity supports the output property.
     * @param matchedEntity entity being validated.
     * @param propertyNestings properties of the entity being accessed.
     * @returns The error type that was encountered. If there is no error, undefined is returned.
     */
    protected checkPropertyValidity(matchedEntity: ILocatedEntity, propertyNestings: IProperty[]): ResolutionErrorKind {
        if (matchedEntity.isExcluded &&
            propertyNestings &&
            propertyNestings.length > 0 &&
            propertyNestings[0].property.startsWith('output')) {
            return this._entityFoundExcludedError;
        }

        return undefined;
    }
}
